<template>
  <div>
    <h2>Bild-Tagging / Training</h2>

    <v-card class="pa-4 mb-4">
      <v-row>
        <v-col cols="12" md="4">
          <v-switch
            v-model="tagForm.tag_all_images"
            label="Alle Bilder taggen"
            color="primary"
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-switch
            v-model="tagForm.trainmodels"
            label="Modelle trainieren"
            color="primary"
          />
        </v-col>

        <v-col cols="12" md="4">
          <v-switch
            v-model="tagForm.newest_first"
            label="Neueste zuerst"
            color="primary"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" md="4">
          <v-text-field
            v-model.number="tagForm.anzahl_Bilder"
            label="Anzahl Unknowns laden"
            type="number"
            min="1"
          />
        </v-col>

        <v-col cols="12" md="8">
          <v-text-field
            v-model="imageIdsInput"
            label="Bild-IDs (kommagetrennt, z. B. 12,15,99)"
            :disabled="tagForm.tag_all_images"
            hint="Nur verwenden, wenn nicht alle Bilder getaggt werden sollen"
            persistent-hint
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12" class="d-flex ga-2">
          <v-btn
            color="primary"
            :loading="isLoading"
            @click="startTagging"
          >
            Tagging starten
          </v-btn>

          <v-btn
            variant="outlined"
            :loading="isLoadingUnknowns"
            @click="loadUnknowns"
          >
            Unknowns neu laden
          </v-btn>

          <v-btn
            variant="outlined"
            :loading="isLoadingStats"
            @click="loadTrainingStats"
          >
            Trainings-Statistik laden
          </v-btn>
        </v-col>
      </v-row>

      <v-alert
        v-if="apiResult"
        type="success"
        class="mt-4"
        variant="tonal"
      >
        {{ apiResult }}
      </v-alert>

      <v-alert
        v-if="apiError"
        type="error"
        class="mt-4"
        variant="tonal"
      >
        {{ apiError }}
      </v-alert>
    </v-card>

    <!-- Statistik / Analyse -->
    <v-card class="pa-4 mb-4">
      <div class="d-flex align-center justify-space-between mb-3">
        <div>
          <strong>Trainings-Statistik</strong>
          <div class="text-caption">
            Pro Person/Verzeichnis: akzeptierte und ungeeignete Trainingsbilder
          </div>
        </div>

        <div class="text-caption" v-if="trainingStatsSummary">
          Gesamt gut: {{ trainingStatsSummary.total_good }} |
          Gesamt schlecht: {{ trainingStatsSummary.total_bad }} |
          Personen: {{ trainingStatsSummary.total_persons }}
        </div>
      </div>

      <v-progress-linear
        v-if="isLoadingStats"
        indeterminate
        class="mb-3"
      />

      <v-alert
        v-if="!isLoadingStats && trainingStatsRows.length === 0"
        type="info"
        variant="tonal"
      >
        Noch keine Trainings-Statistik vorhanden.
      </v-alert>

      <v-table v-else density="compact">
        <thead>
          <tr>
            <th class="text-left">Person / Verzeichnis</th>
            <th class="text-left">Gut</th>
            <th class="text-left">Schlecht</th>
            <th class="text-left">Gesamt</th>
            <th class="text-left">Quote gut</th>
            <th class="text-left">Ordner</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in trainingStatsRows"
            :key="row.person"
          >
            <td>{{ row.person }}</td>
            <td>
              <v-chip color="success" size="small" variant="tonal">
                {{ row.good_count }}
              </v-chip>
            </td>
            <td>
              <v-chip color="error" size="small" variant="tonal">
                {{ row.bad_count }}
              </v-chip>
            </td>
            <td>{{ row.total_count }}</td>
            <td>{{ row.good_ratio }}</td>
            <td class="text-caption">{{ row.folder }}</td>
          </tr>
        </tbody>
      </v-table>
    </v-card>

    <div class="mb-2">
      <strong>Unknowns</strong>
      <div class="text-caption">
        Hinweis: Wenn nur 20 kommen, ist im API-Testlimit noch eine Begrenzung gesetzt.
      </div>
    </div>

    <v-progress-linear
      v-if="isLoading || isLoadingUnknowns"
      :indeterminate="true"
      class="mb-4"
    />

    <v-row>
      <v-col
        v-for="u in unknowns"
        :key="u.id || u.Bild_URL"
        cols="12"
        sm="6"
        md="4"
      >
        <v-card class="pa-2">
          <div class="mb-2">
            <div><strong>ID:</strong> {{ u.id || '-' }}</div>
            <div><strong>Datei:</strong> {{ u.Bilder_Benennung || '-' }}</div>
          </div>

          <div
            @click="showImage(u.Bild_URL)"
            style="cursor: pointer;"
          >
            <v-img
              :src="u.Bild_URL"
              height="220"
              cover
            />
          </div>

          <v-checkbox
            v-if="!tagForm.tag_all_images"
            v-model="selectedImageIds"
            :value="u.id"
            label="Für Tagging auswählen"
            hide-details
          />
        </v-card>
      </v-col>
    </v-row>

    <v-dialog
      v-model="dialog"
      fullscreen
      transition="dialog-bottom-transition"
      :overlay="false"
    >
      <v-card>
        <v-toolbar dark color="primary">
          <v-btn icon @click="dialog = false" dark>
            <v-icon icon="mdi-close"></v-icon>
          </v-btn>
          <v-toolbar-title>Vollbild</v-toolbar-title>
        </v-toolbar>

        <v-card-text>
          <div class="mb-4">URL: {{ VollbildURL }}</div>
          <v-img :src="VollbildURL" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  name: 'TrainObjectsScreen',

  data() {
    return {
      dialog: false,
      VollbildURL: '',
      isLoading: false,
      isLoadingUnknowns: false,
      isLoadingStats: false,
      apiResult: '',
      apiError: '',

      imageIdsInput: '',
      selectedImageIds: [],

      tagForm: {
        anzahl_Bilder: 20,
        trainmodels: false,
        tag_all_images: false,
        newest_first: true,
      },
    };
  },

  async created() {
    await this.loadUnknowns();
    await this.loadTrainingStats();
  },

  computed: {
    ...mapGetters({
      unknowns: 'stateUnknowns',
      trainingStats: 'stateTrainingStats',
    }),

    parsedImageIds() {
      const fromInput = this.imageIdsInput
        .split(',')
        .map(v => parseInt(v.trim(), 10))
        .filter(v => !isNaN(v));

      return [...new Set([...fromInput, ...this.selectedImageIds])];
    },

    trainingStatsRows() {
      const rows = this.trainingStats?.items || [];

      return rows.map(row => {
        const total = Number(row.total_count || 0);
        const good = Number(row.good_count || 0);
        const ratio = total > 0 ? `${((good / total) * 100).toFixed(1)} %` : '0.0 %';

        return {
          person: row.person || '-',
          good_count: good,
          bad_count: Number(row.bad_count || 0),
          total_count: total,
          good_ratio: ratio,
          folder: row.folder || '-',
        };
      });
    },

    trainingStatsSummary() {
      return this.trainingStats?.summary || null;
    },
  },

  methods: {
    async loadUnknowns() {
      this.apiError = '';
      this.isLoadingUnknowns = true;

      try {
        await this.$store.dispatch('getUnknowns', {
          anzahl_Bilder: this.tagForm.anzahl_Bilder,
        });
      } catch (e) {
        this.apiError = 'Unknowns konnten nicht geladen werden.';
      } finally {
        this.isLoadingUnknowns = false;
      }
    },

    async loadTrainingStats() {
      this.apiError = '';
      this.isLoadingStats = true;

      try {
        await this.$store.dispatch('getTrainingStats');
      } catch (e) {
        console.error(e);
        this.apiError = 'Trainings-Statistik konnte nicht geladen werden.';
      } finally {
        this.isLoadingStats = false;
      }
    },

    async startTagging() {
      this.apiError = '';
      this.apiResult = '';

      const payload = {
        trainmodels: this.tagForm.trainmodels,
        tag_all_images: this.tagForm.tag_all_images,
        newest_first: this.tagForm.newest_first,
        image_ids: this.tagForm.tag_all_images ? [] : this.parsedImageIds,
        anzahl_Bilder: this.tagForm.anzahl_Bilder,
      };

      if (!payload.tag_all_images && payload.image_ids.length === 0) {
        this.apiError = 'Bitte mindestens eine Bild-ID auswählen oder "Alle Bilder taggen" aktivieren.';
        return;
      }

      this.isLoading = true;

      try {
        const response = await this.$store.dispatch('tagfaces', payload);
        console.log('tagging response', response);

        this.apiResult = 'Tagging erfolgreich gestartet.';
        await this.loadUnknowns();
        await this.loadTrainingStats();
      } catch (e) {
        console.error(e);
        this.apiError = 'Tagging fehlgeschlagen.';
      } finally {
        this.isLoading = false;
      }
    },

    showImage(url) {
      this.VollbildURL = url;
      this.dialog = true;
    },
  },
};
</script>